'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { generateBlogKey, toBlogCardPreview } from '@eliadmin/core/src/utils/blog-utils';
import { formatContentDate } from '@eliadmin/core/src/utils/date-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { BLOGS_CONFIG } from '@eliadmin/core/src/constants/blogs-config';
import React from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import { CacheKey } from '@eliadmin/core/src/api/constants';

const [BlogCategoriesProvider, useBlogCategories] = createContextStore<
  { categories: BlogCategory[]; activeCategoryIds: string[] },
  { setActiveCategories: (categoryIds: string[]) => void }
>(
  {
    categories: [],
    activeCategoryIds: [],
  },
  set => ({
    setActiveCategories: (categoryIds: string[]) => set({ activeCategoryIds: categoryIds }),
  }),
);

export { BlogCategoriesProvider, useBlogCategories };

export type BlogCardsProps = {
  locale: string;
  query: FetchBlogsQuery;
  className?: {
    blog?: {
      main?: string;
      link?: string;
      image?: string;
      title?: string;
      date?: string;
      contentContainer?: string;
      content?: string;
    };
  };
};

export const BlogCards = ({ locale, query: queryProp, className }: BlogCardsProps) => {
  const { t } = useTranslations();

  const searchParams = useSearchParams();

  const search = searchParams.get('search');
  const activeCategories = useBlogCategories(s => s.activeCategoryIds);
  const { me } = useCustomerStore();

  const query = useMemo<FetchBlogsQuery>(
    () => ({
      // Explicit caller filters still take precedence over the category picker.
      where:
        !('filter' in queryProp) && activeCategories.length > 0
          ? { categories: { in: activeCategories } }
          : undefined,
      search: search ?? undefined,
      ...queryProp,
    }),
    [activeCategories, queryProp, search],
  );

  const fetchBlogsFn = async ({ pageParam = 1 }) => {
    const { data } = await fetchBlogs({
      locale,
      query: { ...query, page: pageParam },
      authToken: me?.authToken,
    });
    return data.map(toBlogCardPreview);
  };

  const {
    data: blogs,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery({
    initialPageParam: 1,
    queryKey: [CacheKey.BLOGS, locale, query, me?.authToken ?? null, 'card-preview'],
    queryFn: fetchBlogsFn,
    getNextPageParam: (lastPage, pages) => pages.length + 1,
  });

  return (
    <div className="flex w-full flex-col">
      <div
        className={cn(
          'flex flex-wrap justify-center gap-4 md:justify-start',
          isFetching && 'opacity-30',
        )}
      >
        {blogs?.pages.map(p => {
          return p.map((blog, i) => {
            if (i === 0 && query.search) return;
            return (
              <Card
                key={generateBlogKey(blog)}
                className={cn(
                  'relative w-[400px] max-w-[90vw]',
                  'ea-blog-card',
                  className?.blog?.main,
                )}
              >
                <Link
                  href={ROUTES.BLOG_DETAIL({ slug: blog.slug }) ?? '#'}
                  className={cn('ea-blog-card__link', className?.blog?.link)}
                  prefetch={false}
                >
                  <CardContent
                    className={cn(
                      'p-0',
                      'ea-blog-card__content',
                      className?.blog?.contentContainer,
                    )}
                  >
                    <Image
                      alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
                      src={blog.image?.url || '/images/placeholder.png'}
                      width="0"
                      height="0"
                      sizes={buildImageSizes([{ min: 'sm', size: '400px' }, { size: '90vw' }])}
                      className={cn(
                        'h-[225px] w-full object-cover object-center',
                        'ea-blog-card__image',
                        className?.blog?.image,
                      )}
                    />
                    <div
                      className={cn(
                        'flex flex-col space-y-4 p-4 pt-0',
                        'ea-blog-card__content-container',
                        className?.blog?.contentContainer,
                      )}
                    >
                      <Badge
                        size={'md'}
                        className={cn(
                          'pointer-events-none flex flex-grow-0 items-center gap-1 rounded-md bg-gray-100 px-2 text-xs font-semibold leading-5 text-foreground opacity-80',
                          'ea-blog-card__date-badge',
                          'absolute left-4 top-4',
                          // className.blog?
                        )}
                      >
                        {React.cloneElement(BLOGS_CONFIG.blogCard.calendarIcon as any, {
                          className: cn('ea-availability-label__icon'),
                        })}
                        {formatContentDate(blog.created_at, locale)}
                      </Badge>

                      <h2
                        data-ea-visual-tag="h6"
                        className={cn(
                          'line-clamp-2 h-[34px] text-ellipsis font-bold leading-4',
                          'ea-blog-card__title',
                          className?.blog?.title,
                        )}
                      >
                        {blog.name}
                      </h2>
                      <p
                        className={cn(
                          'line-clamp-3 h-12 text-ellipsis text-sm leading-4 text-muted-foreground',
                          'ea-blog-card__content',
                          className?.blog?.content,
                        )}
                      >
                        {blog.content}
                      </p>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            );
          });
        })}
      </div>
      {blogs?.pages.length === 1 && blogs?.pages?.every(page => page.length === 0) && (
        <div className="flex w-full items-center justify-center">
          <p className="text-foreground">{t('text554')}</p>
        </div>
      )}
      <div className="flex justify-center">
        {blogs?.pages.length > 0 && blogs?.pages[blogs?.pages.length - 1].length > 0 && (
          <Button
            onClick={() => fetchNextPage()}
            variant={'outline'}
            disabled={isFetching}
            className=""
          >
            {t('text588')}
          </Button>
        )}
      </div>
    </div>
  );
};
